import { type AppHostFactory, type AppHostOptions, PortAppHost } from "./AppHost.js";

/**
 * The prototype's app host: one dedicated Web Worker per app instance.
 *
 * Read §4.4 before relying on this for anything but first-party code. A Worker
 * runs in the shell's own origin — it can open our IndexedDB, our Cache Storage
 * and our OPFS, and `fetch` same-origin endpoints with our cookies. Not touching
 * the DOM is not isolation. This is acceptable through phase 5 because every
 * app is first-party, and phase 6 replaces it with `IframeHost`.
 *
 * Everything a Worker implies is confined to this file.
 */
export class WorkerHost extends PortAppHost {
  #worker: Worker | null = null;

  protected override connect(): MessagePort {
    if (this.source.kind !== "module") {
      throw new Error("WorkerHost needs a module source");
    }
    const channel = new MessageChannel();
    const worker = new Worker(this.source.url, { type: "module", name: this.window.appId });
    this.#worker = worker;

    // An uncaught throw anywhere in the app surfaces here. The shell stays up.
    worker.onerror = (event: ErrorEvent) => {
      event.preventDefault();
      this.fail(event.message === "" ? "app worker failed" : event.message);
    };
    worker.onmessageerror = () => this.fail("app sent a value that could not be deserialised");

    // The bootstrap hand-off: the app's entire world is this one port.
    worker.postMessage(null, [channel.port2]);
    return channel.port1;
  }

  protected override closeIsolate(): void {
    const worker = this.#worker;
    if (worker === null) return;
    this.#worker = null;
    worker.onerror = null;
    worker.onmessageerror = null;
    worker.terminate();
  }
}

export const createWorkerHost: AppHostFactory = (options: AppHostOptions) =>
  new WorkerHost(options);
